import { MenuItem, TextField } from "@mui/material";

import {
  FilterPanel,
  OrgFilter,
  SessionIdFilter,
  UserFilter,
  filterInputClassName,
  type FilterOption,
} from "./FilterPanel";

// ``FilterOption`` is intentionally not re-exported from this module — sibling
// callers should import it directly from ``./FilterPanel`` so the type's home
// is unambiguous (mirrors what ``FeedbackFilters.tsx`` already does).

export interface AgentThreadFiltersValues {
  org: FilterOption | null;
  user: FilterOption | null;
  sessionId: string;
  /** Surfaced inside the User dropdown's popup; when true, the user options
   *  exclude Roboto staff and service accounts. See {@link UserFilter}. */
  customerOnly: boolean;
  /** A session "has errors" if any of its messages terminated as ``failed`` or
   *  emitted an ``error`` chunk. Server-side filter — see ``has_errors`` on
   *  ``GET /v1/admin/agentThreads``. */
  hasErrors: "any" | "yes" | "no";
}

interface AgentThreadFiltersProps {
  values: AgentThreadFiltersValues;
  onChange: (values: AgentThreadFiltersValues) => void;
  onSearch: () => void;
  onClear: () => void;
  isLoading: boolean;
  orgOptions: FilterOption[];
  userOptions: FilterOption[];
  orgOptionsLoading: boolean;
  userOptionsLoading: boolean;
}

export function AgentThreadFilters({
  values,
  onChange,
  onSearch,
  onClear,
  isLoading,
  orgOptions,
  userOptions,
  orgOptionsLoading,
  userOptionsLoading,
}: AgentThreadFiltersProps) {
  return (
    <FilterPanel onSearch={onSearch} onClear={onClear} isLoading={isLoading}>
      <OrgFilter
        value={values.org}
        onChange={(org) => onChange({ ...values, org })}
        options={orgOptions}
        loading={orgOptionsLoading}
      />
      <UserFilter
        value={values.user}
        onChange={(user) => onChange({ ...values, user })}
        options={userOptions}
        loading={userOptionsLoading}
        customerOnly={values.customerOnly}
        onCustomerOnlyChange={(customerOnly) =>
          onChange({ ...values, customerOnly })
        }
      />
      <SessionIdFilter
        value={values.sessionId}
        onChange={(sessionId) => onChange({ ...values, sessionId })}
      />
      <TextField
        className={filterInputClassName}
        select
        label="Has errors?"
        size="small"
        value={values.hasErrors}
        onChange={(e) =>
          onChange({
            ...values,
            hasErrors: e.target.value as AgentThreadFiltersValues["hasErrors"],
          })
        }
      >
        <MenuItem value="any">Any</MenuItem>
        <MenuItem value="yes">Yes</MenuItem>
        <MenuItem value="no">No</MenuItem>
      </TextField>
    </FilterPanel>
  );
}
